import styled from "@emotion/styled"
import { PHeading, PText } from "@porsche-design-system/components-react"
import type { PHeadingProps, PTextProps } from "@porsche-design-system/components-react"
import { spacingFluidXs, spacingFluidSm, spacingFluidMd } from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren } from "react"
import { FormattedMessage } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { FormLayout, FormLayoutGroup } from "src/components/form"
import { CheckboxField } from "src/components/form/checkbox/CheckboxField"
import { DatepickerField } from "src/components/form/datepicker/DatepickerField"
import { InputField } from "src/components/form/input/InputField"
import { InputYearMonthField } from "src/components/form/inputYearMonth"
import { LocalizedTextAreaField } from "src/components/form/localizedTextAreaField/LocalizedTextAreaField"
import { RadioGroupField } from "src/components/form/radio/radioGroup/RadioGroupField"
import { SelectField } from "../../../form/select/SelectField"
import { TextAreaField } from "../../../form/textArea/TextAreaField"
import type { ConditionAndServiceFields } from "../types/ConditionAndServiceFields"
import { conditionAndServiceFormMessages } from "./conditionAndServiceForm.messages"

interface ConditionAndServiceFormProps {
	fields: ConditionAndServiceFields
}

export const ConditionAndServiceForm = ({ fields }: ConditionAndServiceFormProps) => {
	return (
		<FormLayoutGroup>
			{/* General information */}
			<Group>
				<Heading>
					<FormattedMessage {...conditionAndServiceFormMessages.generalInformationTitle} />
				</Heading>
				<SubHeading>
					<FormattedMessage {...commonMessages.visibleForCustomers} />
				</SubHeading>
				<FormLayout $width="quarter">
					{fields.firstRegistrationDate && (
						<DatepickerField
							data-testid="condition-and-service-form_first-registration-date"
							{...fields.firstRegistrationDate}
						/>
					)}
					<InputField {...fields.mileage} />
					<SelectField {...fields.previousOwners} />
					{fields.previousOwnerUsageHistory && <SelectField {...fields.previousOwnerUsageHistory} />}
				</FormLayout>
			</Group>
			{(!!fields.carPassUrlFR || !!fields.carPassUrlNL) && (
				<Group>
					<Heading>
						<FormattedMessage {...conditionAndServiceFormMessages.carPassTitle} />
					</Heading>
					<SubHeading>
						<FormattedMessage {...conditionAndServiceFormMessages.carPassSubtitle} />
					</SubHeading>

					<FormLayout $width="quarter">
						{fields.carPassUrlFR && <InputField {...fields.carPassUrlFR} />}
						{fields.carPassUrlNL && <InputField {...fields.carPassUrlNL} />}
					</FormLayout>
				</Group>
			)}

			{/* Battery capacity */}
			{(!!fields.batteryCapacityPercentage ||
				!!fields.batteryCapacityDate ||
				!!fields.batteryCapacityMileage) && (
				<Group>
					<Heading>
						<FormattedMessage {...conditionAndServiceFormMessages.batteryInformationTitle} />
					</Heading>
					<FormLayout $width="quarter">
						{fields.batteryCapacityPercentage && <InputField {...fields.batteryCapacityPercentage} />}
						{fields.batteryCapacityDate && <DatepickerField {...fields.batteryCapacityDate} />}
						{fields.batteryCapacityMileage && <InputField {...fields.batteryCapacityMileage} />}
					</FormLayout>
				</Group>
			)}

			{/* Damage History */}
			<Group>
				<Heading>
					<FormattedMessage {...conditionAndServiceFormMessages.damageHistoryTitle} />
				</Heading>
				<SubHeading>
					<FormattedMessage {...commonMessages.visibleForCustomers} />
				</SubHeading>

				<FormLayout $width="quarter">
					{fields.accidentFree && <RadioGroupField {...fields.accidentFree} />}
					{fields.knownAccidents && <RadioGroupField {...fields.knownAccidents} />}
				</FormLayout>
				<FormLayout $width="half">
					{fields.accidentDescription && (
						<LocalizedTextAreaField {...fields.accidentDescription} locales="customer-app" />
					)}
				</FormLayout>
			</Group>

			{/* Service History */}
			<Group>
				<Heading>
					<FormattedMessage {...conditionAndServiceFormMessages.serviceHistoryTitle} />
				</Heading>
				<SubHeading>
					<FormattedMessage {...commonMessages.visibleForCustomers} />
				</SubHeading>
				<FormLayout $width="quarter">
					<RadioGroupField {...fields.fullServiceHistory} />
					{fields.fullServiceHistoryInPorscheCenter && (
						<IndentedCheckboxField {...fields.fullServiceHistoryInPorscheCenter} />
					)}
				</FormLayout>

				{/* Last maintenance */}
				<LastMaintenanceFormLayout $width="half">
					<div>
						<PHeading size="sm">
							<FormattedMessage {...conditionAndServiceFormMessages.lastMaintenanceTitle} />
						</PHeading>
						<PText color="contrast-medium" size="xs">
							<FormattedMessage {...conditionAndServiceFormMessages.lastMaintenanceSubtitleNew} />
						</PText>
					</div>
				</LastMaintenanceFormLayout>

				<FormLayout $width="quarter">
					<DatepickerField {...fields.latestMaintenanceDate} />
					<RadioGroupField {...fields.latestMaintenanceScope} />
					{fields.latestMaintenanceMileage && <InputField {...fields.latestMaintenanceMileage} />}
					{fields.latestMaintenanceWorkshop && <InputField {...fields.latestMaintenanceWorkshop} />}
				</FormLayout>
			</Group>

			{/* Pre-contractual Information */}
			<Group>
				<Heading>
					<FormattedMessage {...conditionAndServiceFormMessages.precontractualInformation} />
				</Heading>
				<SubHeading>
					<FormattedMessage {...commonMessages.visibleInternally} />
				</SubHeading>
				<FormLayoutGroup>
					<FormLayout $width="quarter">
						<InputYearMonthField {...fields.nextGeneralInspectionDate} />
					</FormLayout>

					<FormLayout $width="quarter">
						<CheckboxField {...fields.renewGeneralInspectionOnSale} />
						<CheckboxField {...fields.nonSmoker} />
						<CheckboxField {...fields.formerRentalCar} />
						<CheckboxField {...fields.previousDamage} />
						{fields.previousDamageAmount && <InputField {...fields.previousDamageAmount} />}
					</FormLayout>
					{fields.previousDamageDescription && (
						<FormLayout $width="half">
							<TextAreaField {...fields.previousDamageDescription} />
						</FormLayout>
					)}

					<FormLayout $width="quarter">
						<RadioGroupField {...fields.reconditioned} />
						{fields.reconditioningAmount && <InputField {...fields.reconditioningAmount} />}
						{fields.reconditioningApproximateAmount && (
							<InputField {...fields.reconditioningApproximateAmount} />
						)}
					</FormLayout>
				</FormLayoutGroup>
			</Group>
		</FormLayoutGroup>
	)
}

const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingFluidSm};
`

const Heading = styled(({ children, ...rest }: PropsWithChildren<PHeadingProps>) => (
	<PHeading size="md" {...rest}>
		{children}
	</PHeading>
))`
	grid-column: 1 / -1;
`

const SubHeading = styled(({ children, ...rest }: PropsWithChildren<PTextProps>) => (
	<PText color="contrast-medium" size="xs" {...rest}>
		{children}
	</PText>
))`
	grid-column: 1 / -1;
	margin-top: calc(-1 * ${spacingFluidSm});
`

const IndentedCheckboxField = styled(CheckboxField)`
	padding-inline-start: ${spacingFluidMd};
`

const LastMaintenanceFormLayout = styled(FormLayout)`
	gap: ${spacingFluidXs};
`
